import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import sq from "date-fns/locale/sq";
import { CaptionProps, DateRange, DayPicker, useNavigation } from "react-day-picker";
import styles from "../../ui/datepicker/style.module.css";

// Month name between the two arrows. The shared picker's caption is built for its two-month,
// full-width modal (it adds "Datë tjetër" inputs and pulls them out with a negative margin), so
// inside a filter panel it overflows.
const MonthCaption = ({ displayMonth }: CaptionProps) => {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const arrow = "w-8 h-8 rounded-full ring-1 ring-gray-300 flex items-center justify-center "
    + "duration-200 hover:ring-gray-500 disabled:opacity-30";
  return (
    <div className="mb-2 flex items-center justify-between px-1">
      <button
        type="button"
        aria-label="Muaji i kaluar"
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className={arrow}
      >
        <ChevronLeftIcon className="w-4 h-4" />
      </button>
      <p className="font-semibold capitalize">{format(displayMonth, "LLLL yyyy", { locale: sq })}</p>
      <button
        type="button"
        aria-label="Muaji tjetër"
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className={arrow}
      >
        <ChevronRightIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

interface FilterCalendarProps {
  range?: DateRange;
  onSelect: (range: DateRange | undefined) => void;
}

// One month, range mode, with the picked range spelled out above it.
const FilterCalendar = ({ range, onSelect }: FilterCalendarProps) => {
  const day = (d?: Date) => (d ? format(d, "dd.MM.yyyy") : "");
  let summary = "Zgjedh ditën e parë";
  if (range?.from) {
    summary = range.to && day(range.to) !== day(range.from)
      ? `${day(range.from)} – ${day(range.to)}`
      : `${day(range.from)} · zgjedh ditën e fundit ose lëre një ditë`;
  }

  return (
    <div className="flex flex-col">
      <p className="mb-2 rounded-md bg-gray-50 px-2 py-1.5 text-center text-xs text-neutral">
        {summary}
      </p>
      <DayPicker
        mode="range"
        locale={sq}
        classNames={styles}
        defaultMonth={range?.from}
        selected={range}
        onSelect={onSelect}
        components={{ Caption: MonthCaption }}
      />
    </div>
  );
};

export default FilterCalendar;
